import { useCallback, useRef, useState } from 'react'
import { browserHistory } from 'react-router'
import { toast } from 'react-toastify'
import { syncToNoyo } from 'enrollment-platform/api/partner-benefit-graph/client'
import { classifyNoyoSyncError } from 'routes/Employees/Employee/EmployeeCoverage/hooks/employer-noyo'

const SYNC_ERROR_MESSAGE = {
  'not-enabled': 'Noyo sync is not enabled for this client.',
  'in-progress': 'A Noyo sync is already in progress for this employee.',
  failed: 'The Noyo sync could not be started.',
} as const

export type AncillaryNoyoSyncInput = {
  employeePublicKey: string
  employmentStatus?: string | null
}

export function useAncillaryNoyoSync() {
  const [syncingEmployeePublicKeys, setSyncingEmployeePublicKeys] = useState<
    ReadonlySet<string>
  >(() => new Set())
  const inFlightEmployeePublicKeys = useRef(new Set<string>())

  const syncEmployee = useCallback(async (input: AncillaryNoyoSyncInput) => {
    if (inFlightEmployeePublicKeys.current.has(input.employeePublicKey)) return
    inFlightEmployeePublicKeys.current.add(input.employeePublicKey)
    setSyncingEmployeePublicKeys(new Set(inFlightEmployeePublicKeys.current))
    try {
      await syncToNoyo({
        employeePublicKey: input.employeePublicKey,
        employmentStatus: input.employmentStatus,
      })
      toast.success('Sync enqueued, click to view benefit integrations', {
        position: 'top-right',
        onClick: () => {
          browserHistory.push('/benefit-integrations')
        },
      })
    } catch (error) {
      toast.error(SYNC_ERROR_MESSAGE[classifyNoyoSyncError(error)])
    } finally {
      inFlightEmployeePublicKeys.current.delete(input.employeePublicKey)
      setSyncingEmployeePublicKeys(new Set(inFlightEmployeePublicKeys.current))
    }
  }, [])

  return { syncEmployee, syncingEmployeePublicKeys }
}
